import { useState } from 'react';
import type { WheelSpinStatus, WheelSpinsSummary } from '@/api/types';
import { formatCount, formatPercent } from '@/lib/format';
import { Card } from '@/ui/Card';
import { Stat } from '@/ui/Stat';
import { Table, Td, Th } from '@/ui/Table';
import { STATUS_LABEL } from './spin-status';
import { NEEDS_ATTENTION } from './wheel-filters';

const PRIZES_SHOWN = 8;

const spinsWith = (summary: WheelSpinsSummary, statuses: readonly WheelSpinStatus[]) =>
  summary.byStatus.filter(({ status }) => statuses.includes(status)).reduce((sum, { spins }) => sum + spins, 0);

const shareOf = (part: number, whole: number) => (whole ? formatPercent(part / whole) : '–');

export function WheelStats({ summary }: { summary: WheelSpinsSummary }) {
  const granted = spinsWith(summary, ['granted']);
  const attention = spinsWith(summary, NEEDS_ATTENTION);
  const pending = spinsWith(summary, ['pending_choice']);
  const declined = spinsWith(summary, ['declined']);
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <Stat
        label="Spins"
        value={formatCount(summary.spins)}
        hint={[
          `by ${formatCount(summary.players)} players`,
          ...summary.byTier.map(({ tier, spins }) => `${tier} ${formatCount(spins)}`),
        ].join(' · ')}
      />
      <Stat
        label={STATUS_LABEL.granted}
        value={formatCount(granted)}
        hint={`${shareOf(granted, summary.spins)} of spins`}
      />
      <Stat
        label="Needs attention"
        value={formatCount(attention)}
        hint={`${STATUS_LABEL.failed} ${formatCount(spinsWith(summary, ['failed']))} · ${STATUS_LABEL.drawn.toLowerCase()} ${formatCount(spinsWith(summary, ['drawn']))}`}
      />
      <Stat
        label={STATUS_LABEL.pending_choice}
        value={formatCount(pending)}
        hint={`${STATUS_LABEL.declined.toLowerCase()} ${formatCount(declined)}`}
      />
    </div>
  );
}

interface PrizeBreakdownProps {
  summary: WheelSpinsSummary;
  onPick(prize: { wheelUid: string; segmentUid: string }): void;
}

// How the matching spins split over the prizes, to check a wheel pays out the way its weights say.
export function PrizeBreakdown({ summary, onPick }: PrizeBreakdownProps) {
  const [showAll, setShowAll] = useState(false);
  const prizes = showAll ? summary.byPrize : summary.byPrize.slice(0, PRIZES_SHOWN);

  return (
    <Card title="Prizes" description="Share of the spins that match the filters. Click a prize to filter by it.">
      <Table>
        <thead>
          <tr>
            <Th align="left">Prize</Th>
            <Th align="left">Wheel</Th>
            <Th>Spins</Th>
            <Th>Share</Th>
          </tr>
        </thead>
        <tbody>
          {prizes.length === 0 && (
            <tr>
              <Td align="left" colSpan={4} className="text-slate-500">
                No spins match these filters.
              </Td>
            </tr>
          )}
          {prizes.map((prize) => (
            <tr key={`${prize.wheelUid}/${prize.segmentUid}`}>
              <Td align="left">
                <button
                  type="button"
                  className="text-left text-blue-700 hover:underline"
                  onClick={() => onPick({ wheelUid: prize.wheelUid, segmentUid: prize.segmentUid })}
                >
                  {prize.segmentLabel ?? prize.segmentUid}
                </button>
                {prize.segmentLabel && (
                  <span className="ml-2 font-mono text-xs text-slate-400">{prize.segmentUid}</span>
                )}
              </Td>
              <Td align="left">{prize.wheelUid}</Td>
              <Td>{formatCount(prize.spins)}</Td>
              <Td>{shareOf(prize.spins, summary.spins)}</Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {summary.byPrize.length > PRIZES_SHOWN && (
        <button
          type="button"
          className="mt-3 text-sm text-blue-700 hover:underline"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? 'Show fewer' : `Show all ${summary.byPrize.length} prizes`}
        </button>
      )}
    </Card>
  );
}
